'use client';

import { useTranslations } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { resolveApiErrorToastMessage } from '../lib/api/localizeApiError';
import {
  addTeamMember,
  fetchTeamMembers,
  removeTeamMember,
  updateTeamMemberRole,
  type MemberInvitationResult,
  type TeamMember,
  type TeamRole,
} from '../lib/api';
import { getAuthSession } from '../lib/authSession';
import {
  canManageOwnerRole,
  canManageTeamMembers,
  countAdminMembers,
  findCurrentTeamMembership,
} from '../lib/permissions';
import { MemberListItemSkeleton } from './MemberListItemSkeleton';
import { Select } from './Select';
import DeleteConfirmModal from './DeleteConfirmModal';
import { queryKeys } from '../hooks/queries/queryKeys';
import { Spinner } from './loading/Spinner';

const isMemberInvitationResult = (value: TeamMember | MemberInvitationResult): value is MemberInvitationResult => {
  return 'invited' in value;
};

type TeamMembersViewProps = {
  teamId: string;
};

export default function TeamMembersView({ teamId }: TeamMembersViewProps) {
  const t = useTranslations('teams.members');
  const tCommon = useTranslations('common');
  const tRoot = useTranslations();
  const queryClient = useQueryClient();
  const session = useMemo(() => getAuthSession()!, []);

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<TeamRole>('MEMBER');
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [updatingMemberId, setUpdatingMemberId] = useState<string | null>(null);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [confirmRemoveMemberId, setConfirmRemoveMemberId] = useState<string | null>(null);

  const currentMembership = useMemo(() => {
    return findCurrentTeamMembership(members, session.member.id);
  }, [members, session.member.id]);

  const currentRole = currentMembership?.role;
  const canManage = canManageTeamMembers(currentRole);
  const canManageOwner = canManageOwnerRole(currentRole);

  const editableRoleOptions = canManageOwner
    ? (['OWNER', 'ADMIN', 'MEMBER'] as TeamRole[])
    : (['ADMIN', 'MEMBER'] as TeamRole[]);

  const roleOptionsForMember = (memberRole: TeamRole) => {
    if (canManageOwner || memberRole !== 'OWNER') {
      return editableRoleOptions;
    }
    return ['OWNER', ...editableRoleOptions] as TeamRole[];
  };

  useEffect(() => {
    if (!teamId) {
      toast.error(t('toastTeamIdMissing'));
      setIsLoading(false);
      return;
    }

    const load = async () => {
      setIsLoading(true);
      try {
        const foundMembers = await fetchTeamMembers(session.token, teamId);
        setMembers(foundMembers);
      } catch (loadError) {
        const message = resolveApiErrorToastMessage(tRoot, loadError);
        if (message) toast.error(message);
      } finally {
        setIsLoading(false);
      }
    };

    void load();
  }, [session.token, teamId, t, tRoot]);

  const handleAdd = async () => {
    if (!canManage) {
      toast.error(t('toastPermissionManage'));
      return;
    }
    if (!inviteEmail.trim()) return;

    setIsAdding(true);
    try {
      const result = await addTeamMember(session.token, teamId, {
        email: inviteEmail.trim(),
        role: inviteRole,
      });
      if (isMemberInvitationResult(result)) {
        toast.success(t('toastInviteSent'));
        setInviteEmail('');
        return;
      }
      const added = result;
      setMembers((prev) => {
        const filtered = prev.filter((m) => m.memberId !== added.memberId);
        return [...filtered, added];
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.teams.members(teamId) });
      setInviteEmail('');
    } catch (submitError) {
      const message = resolveApiErrorToastMessage(tRoot, submitError);
      if (message) toast.error(message);
    } finally {
      setIsAdding(false);
    }
  };

  const handleRoleChange = async (memberId: string, role: TeamRole) => {
    if (!canManage) {
      toast.error(t('toastPermissionRole'));
      return;
    }
    const target = members.find((member) => member.memberId === memberId);
    if (!target) return;
    if ((target.role === 'OWNER' || role === 'OWNER') && !canManageOwner) {
      toast.error(t('toastOwnerOnly'));
      return;
    }
    setUpdatingMemberId(memberId);
    try {
      const updated = await updateTeamMemberRole(session.token, teamId, memberId, role);
      setMembers((prev) => prev.map((m) => (m.memberId === updated.memberId ? updated : m)));
    } catch (submitError) {
      const message = resolveApiErrorToastMessage(tRoot, submitError);
      if (message) toast.error(message);
    } finally {
      setUpdatingMemberId(null);
    }
  };

  const handleRemove = async (memberId: string) => {
    if (!canManage) {
      toast.error(t('toastPermissionRemove'));
      return;
    }
    const target = members.find((m) => m.memberId === memberId);
    if (!target) return;
    if (target.role === 'OWNER') {
      toast.error(t('toastOwnerCannotRemove'));
      return;
    }
    if (target.role === 'ADMIN' && countAdminMembers(members) <= 1) {
      toast.error(t('toastLastAdmin'));
      return;
    }
    setConfirmRemoveMemberId(memberId);
  };

  const handleConfirmRemove = async () => {
    if (!confirmRemoveMemberId) return;
    const target = members.find((m) => m.memberId === confirmRemoveMemberId);
    if (!target) {
      setConfirmRemoveMemberId(null);
      return;
    }

    setRemovingMemberId(confirmRemoveMemberId);
    try {
      await removeTeamMember(session.token, teamId, confirmRemoveMemberId);
      setMembers((prev) => prev.filter((m) => m.memberId !== confirmRemoveMemberId));
      void queryClient.invalidateQueries({ queryKey: queryKeys.teams.members(teamId) });
      setConfirmRemoveMemberId(null);
    } catch (removeError) {
      const message = resolveApiErrorToastMessage(tRoot, removeError);
      if (message) toast.error(message);
    } finally {
      setRemovingMemberId(null);
    }
  };

  const confirmRemoveTarget = confirmRemoveMemberId
    ? members.find((member) => member.memberId === confirmRemoveMemberId)
    : null;
  const confirmRemoveName = confirmRemoveTarget?.member.email ?? confirmRemoveMemberId ?? '-';

  return (
    <div>
      {!isLoading && !canManage ? <p className="mb-4 text-xs text-warning">{t('permissionHint')}</p> : null}

      <div className="mb-4 flex flex-col gap-3 rounded-2xl border border-base-300 bg-base-100 p-4 md:flex-row md:items-end">
        <div className="form-control flex-1">
          <label className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]" htmlFor="tmv-invite-email">
            {t('email')}
          </label>
          <input
            id="tmv-invite-email"
            type="email"
            className="input input-bordered input-sm w-full"
            placeholder={t('emailPlaceholder')}
            value={inviteEmail}
            onChange={(event) => setInviteEmail(event.target.value)}
          />
        </div>
        <div className="form-control">
          <label className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]" htmlFor="tmv-invite-role">
            {t('role')}
          </label>
          <Select
            id="tmv-invite-role"
            size="sm"
            value={inviteRole}
            onChange={(val) => setInviteRole(val as TeamRole)}
            disabled={!canManage || isLoading}
            options={editableRoleOptions.map((role) => ({ value: role, label: role }))}
          />
        </div>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={!canManage || !teamId || !inviteEmail.trim() || isAdding || isLoading}
          onClick={() => void handleAdd()}
        >
          {isAdding ? t('adding') : t('addMember')}
        </button>
      </div>

      {isLoading ? (
        <MemberListItemSkeleton />
      ) : members.length === 0 ? (
        <div className="rounded-xl border border-dashed border-base-300 p-8 text-center">
          <h3 className="mb-1 font-semibold text-base-content">{t('emptyTitle')}</h3>
          <p className="text-sm text-[var(--color-fg-muted)]">{t('emptyDescription')}</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {members.map((member) => (
            <li
              key={member.id}
              className="flex items-center justify-between rounded-xl border border-base-300 bg-base-100 p-4"
            >
              <div>
                <div className="text-sm font-medium">{member.member.email}</div>
                <div className="text-xs text-[var(--color-fg-subtle)]">{member.member.nickname}</div>
              </div>
              <div className="flex items-center gap-2">
                <Select
                  size="xs"
                  value={member.role}
                  disabled={
                    updatingMemberId === member.memberId || !canManage || (member.role === 'OWNER' && !canManageOwner)
                  }
                  onChange={(val) => void handleRoleChange(member.memberId, val as TeamRole)}
                  options={roleOptionsForMember(member.role).map((role) => ({ value: role, label: role }))}
                />
                <button
                  type="button"
                  aria-label={t('remove')}
                  title={t('remove')}
                  className="btn btn-ghost btn-xs text-error hover:bg-error/10"
                  disabled={
                    !canManage ||
                    member.role === 'OWNER' ||
                    (member.role === 'ADMIN' && countAdminMembers(members) <= 1) ||
                    removingMemberId === member.memberId
                  }
                  onClick={() => void handleRemove(member.memberId)}
                >
                  {removingMemberId === member.memberId ? (
                    <Spinner />
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.5"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3m-4 0h14"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <DeleteConfirmModal
        isOpen={confirmRemoveMemberId !== null}
        title={t('remove')}
        message={t('confirmRemove', { name: confirmRemoveName })}
        confirmLabel={t('remove')}
        cancelLabel={tCommon('actions.cancel')}
        isPending={removingMemberId === confirmRemoveMemberId}
        onConfirm={() => void handleConfirmRemove()}
        onCancel={() => {
          if (removingMemberId === null) {
            setConfirmRemoveMemberId(null);
          }
        }}
      />
    </div>
  );
}
